import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Zap, Trash2, Play, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Automations() {
  const [items, setItems] = useState([]);
  const [topics, setTopics] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({});
  const [dlgTrigger, setDlgTrigger] = useState(false);
  const [triggerForm, setTriggerForm] = useState({ topic: "lead.created", payload: "{}" });

  const load = () => {
    api.get("/automations").then((r) => { setItems(r.data.automations); setTopics(r.data.topics); });
    api.get("/workflows").then((r) => setWorkflows(r.data.workflows));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.event_topic || !form.workflow_id) return toast.error("Rellena todo");
    try {
      await api.post("/automations", form);
      toast.success("Automatización creada");
      setForm({}); setDlg(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fallo"); }
  };

  const toggle = async (a) => { await api.post(`/automations/${a.id}/toggle`); load(); };
  const del = async (id) => { if (!window.confirm("¿Eliminar?")) return; await api.delete(`/automations/${id}`); load(); };

  const trigger = async () => {
    try {
      const payload = JSON.parse(triggerForm.payload || "{}");
      const { data } = await api.post("/automations/trigger", { topic: triggerForm.topic, payload });
      toast.success(`Ejecutadas ${data.automations_run.length} automatizaciones`);
      setDlgTrigger(false);
    } catch (err) { toast.error("Payload inválido o error"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="automations-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">EVENT-DRIVEN AUTOMATION</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Automatizaciones</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Cuando ocurre un evento (deal.won, lead.created, incident.opened…), dispara un workflow completo.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={dlgTrigger} onOpenChange={setDlgTrigger}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none border-[var(--border)] hover:border-[var(--accent)]" data-testid="auto-trigger-btn">
                <Play className="w-3 h-3 mr-2" /> Disparar evento
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
              <DialogHeader><DialogTitle className="font-display">Disparar evento manualmente</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-mono">Topic</Label>
                  <Select value={triggerForm.topic} onValueChange={(v) => setTriggerForm({ ...triggerForm, topic: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="auto-trg-topic"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-h-64">
                      {topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Payload (JSON)</Label>
                  <Input value={triggerForm.payload} onChange={(e) => setTriggerForm({ ...triggerForm, payload: e.target.value })}
                         className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="auto-trg-payload" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={trigger} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="auto-trg-run">Disparar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={dlg} onOpenChange={setDlg}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="auto-add">
                <Zap className="w-4 h-4 mr-2" /> Nueva regla
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
              <DialogHeader><DialogTitle className="font-display">Nueva automatización</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-mono">Nombre</Label>
                  <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })}
                         className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="auto-name" />
                </div>
                <div>
                  <Label className="label-mono">Cuando ocurra…</Label>
                  <Select value={form.event_topic || ""} onValueChange={(v) => setForm({ ...form, event_topic: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="auto-topic"><SelectValue placeholder="Elige un topic" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-h-64">
                      {topics.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">…ejecuta workflow</Label>
                  <Select value={form.workflow_id || ""} onValueChange={(v) => setForm({ ...form, workflow_id: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="auto-wf"><SelectValue placeholder="Elige workflow" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {workflows.length === 0 && <div className="p-3 text-xs text-[var(--text-dim)] font-mono">Crea un workflow primero en /workflows</div>}
                      {workflows.map((w) => <SelectItem key={w.id} value={w.id}>{w.title} ({w.steps.length} pasos)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="auto-submit">Crear</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="auto-empty">
          <Zap className="w-8 h-8 mx-auto text-[var(--text-dim)] mb-3" />
          <p className="text-sm text-[var(--text-dim)] mb-2">Sin reglas. Ejemplo: <span className="font-mono">deal.won → Onboarding cliente enterprise</span>.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const wf = workflows.find((w) => w.id === a.workflow_id);
            return (
              <div key={a.id} className="border border-[var(--border)] bg-[var(--surface)] p-4 flex items-center gap-4" data-testid={`auto-${a.id}`}>
                <Zap className={`w-5 h-5 ${a.enabled ? "text-[var(--accent)]" : "text-[var(--text-dim)]"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-base mb-1">{a.name}</div>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="rounded-none font-mono text-[10px] bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/40">
                      {a.event_topic}
                    </Badge>
                    <ArrowRight className="w-3 h-3 text-[var(--text-dim)]" />
                    <span className="font-mono text-xs text-white">{wf?.title || a.workflow_id}</span>
                    <span className="font-mono text-[10px] text-[var(--text-dim)]">· {a.runs || 0} ejecuciones</span>
                  </div>
                </div>
                <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`auto-toggle-${a.id}`} />
                <button onClick={() => del(a.id)} className="text-[var(--text-dim)] hover:text-[var(--danger)]" data-testid={`auto-del-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
